/**
 * Vertical vote arrows + score — the SO-style triangle stack that sits
 * to the left of the question body and each answer on the detail page.
 *
 * Stateless. Parent owns mutation handlers (the `<QuestionPage>`
 * passes them in so the same component drives both question and
 * answer votes).
 */
export function VoteStack({
  score,
  isUpvoted,
  isDownvoted,
  canVote,
  onUpvote,
  onDownvote,
  pending,
}: {
  score: number;
  isUpvoted: boolean;
  isDownvoted: boolean;
  canVote: boolean;
  onUpvote: () => void;
  onDownvote: () => void;
  pending?: boolean;
}) {
  const arrowBase =
    "w-9 h-9 inline-flex items-center justify-center rounded text-lg leading-none transition";
  const upClasses = isUpvoted
    ? `${arrowBase} bg-accent text-white`
    : `${arrowBase} text-zinc-500 hover:bg-accent/10 hover:text-accent`;
  const downClasses = isDownvoted
    ? `${arrowBase} bg-zinc-600 text-white`
    : `${arrowBase} text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-800`;
  return (
    <div className="flex flex-col items-center select-none">
      <button
        type="button"
        onClick={onUpvote}
        disabled={!canVote || pending}
        className={`${upClasses} disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed`}
        aria-label="Upvote"
        title={canVote ? "Upvote" : "Sign in to vote"}
      >
        ▲
      </button>
      <span
        className={`font-mono font-semibold text-base-dense ${
          score > 0
            ? "text-accent-fg dark:text-accent"
            : score < 0
            ? "text-zinc-500"
            : "text-zinc-700 dark:text-zinc-300"
        }`}
      >
        {score}
      </span>
      <button
        type="button"
        onClick={onDownvote}
        disabled={!canVote || pending}
        className={`${downClasses} disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed`}
        aria-label="Downvote"
        title={canVote ? "Downvote" : "Sign in to vote"}
      >
        ▼
      </button>
    </div>
  );
}
